import React from "react";
import { Link } from "react-router-dom";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white border border-slate-200 rounded-xl ${className}`} {...rest}>{children}</div>;
}

export function Stat({ label, value, tone = "default", icon: Icon, to }) {
  const tones = {
    default: "text-slate-900", red: "text-alto-red", warn: "text-amber-600", good: "text-emerald-600",
  };
  const body = (
    <Card className="p-5 hover:border-slate-300 transition-colors h-full">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon size={16} className="text-slate-400" />}
      </div>
      <div className={`font-display text-3xl font-extrabold mt-3 ${tones[tone]}`}>{value}</div>
    </Card>
  );
  return to ? <Link to={to} data-testid={`stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>{body}</Link> : <div data-testid={`stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>{body}</div>;
}

export function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700", red: "bg-red-50 text-red-700",
    amber: "bg-amber-100 text-amber-800", green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${tones[tone] || tones.slate}`}>{children}</span>;
}

export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div className="text-center py-16">
      {Icon && <Icon className="mx-auto text-slate-300 mb-4" size={40} />}
      <h3 className="font-display text-lg font-bold text-slate-700">{title}</h3>
      {hint && <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function RedButton({ children, className = "", ...rest }) {
  return <button className={`btn-red font-semibold px-4 py-2.5 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 ${className}`} {...rest}>{children}</button>;
}

export function GhostButton({ children, className = "", ...rest }) {
  return <button className={`font-semibold px-4 py-2.5 rounded-md text-sm border border-slate-200 hover:bg-slate-50 text-slate-700 inline-flex items-center justify-center gap-2 ${className}`} {...rest}>{children}</button>;
}

export function Field({ label, children }) {
  return (
    <div>
      {label && <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>}
      {children}
    </div>
  );
}

export const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3.5 py-2.5 text-sm text-slate-900 focus:border-alto-red outline-none";
